import React, {ReactNode, useCallback, useEffect} from "react";
import s from "./GenericModal.module.scss";
import {iconImages} from "../../../config/config";
import {midiUserAction} from "../../../services/midi/midi-user-action";
import PianoKey, {Key} from "../PianoKey";
import type {IMidiNotePlayed} from "../../../services/eventBus/midi/MidiEventTypes";
import {useAppSelector} from "../../../redux/hooks";
import {settingsStore} from "../../../redux/settings/settingsStore";

interface IGenericModalProps {
  backgroundColor?: "white" | "purple";
  canShow: boolean | (() => boolean);
  children?: ReactNode;
  maxWidth?: string;
  navigationNote?: Key;
  onClose?: () => void;
  padBody?: boolean;
  showCloseButtonInBody?: boolean;
  title?: string | ReactNode;
}

/**
 * @param onClose Controls what happens when the close button is pressed. If provided also causes the close button to be displayed.
 * @param canShow A boolean, or a function that returns a boolean that determines if the modal should be shown.
 * @param title The title of the modal.
 * @param backgroundColor The background color of the modal. Can be 'white' or 'purple'.
 * @param maxWidth The maximum width of the modal.
 * @param showCloseButtonInBody If true, a close button will be shown in the body of the modal instead of in the title bar.
 * @param children The content of the modal.
 * @param padBody Whether or not to include padding on the body of the modal.
 * @param navigationNote If provided, a piano key will be shown to the right of the close button, and the modal can
 *  be closed by pressing that key on a connected MIDI keyboard.
 * @constructor
 */
export function GenericModal({children, onClose, showCloseButtonInBody, canShow, title, backgroundColor, maxWidth, navigationNote, padBody = true}: IGenericModalProps) {
  const isMidiKeyboardConnected = useAppSelector(settingsStore.getIsMidiDeviceConnected);
  const canShowModal = typeof canShow === "boolean" ? canShow : canShow();

  if (!maxWidth) {
    maxWidth = 'max(30vw, 345px)';
  }

  useEffect(() => {
    if (onClose) {
      if (navigationNote) {
        return midiUserAction.subscribeToNextMidiNotePlayed(navigationNote, (_midiEvent: IMidiNotePlayed) => {
          onClose();
        });
      }
    }
  }, [navigationNote, onClose]);

  const closeOnEscapeKeypress = useCallback((event) => {
    if (event.key === 'Escape') {
      if (onClose) {
        onClose();
      }
    }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener("keydown", closeOnEscapeKeypress);

    return () => {
      document.removeEventListener("keydown", closeOnEscapeKeypress);
    };
  }, [closeOnEscapeKeypress]);

  if (!canShowModal) {
    return null;
  }

  backgroundColor = backgroundColor || 'white';

  const canShowTitleBar = title || (onClose && !showCloseButtonInBody);

  return (
    <div className={s.genericModal}>
      <div className={`${s.contentContainer} ${s[backgroundColor]}`} style={{ maxWidth: maxWidth }}>
        {getHeader(closeOnEscapeKeypress, showCloseButtonInBody, title, onClose, navigationNote, isMidiKeyboardConnected, padBody)}
        <div className={`${s.content} ${padBody ? s.padBody : ''} ${canShowTitleBar ? '' : s.noTitleBar}`}>          
          {            
            showCloseButtonInBody
              ?
              <>
                <div className={s.bodyCloseButtonContainer}>
                  {getCloseButton(closeOnEscapeKeypress, onClose, navigationNote, "body", isMidiKeyboardConnected, padBody)}
                </div>
              </>
              :
              null
          }
          {children}
        </div>
      </div>
    </div>
  );
}

function getHeader(
  closeOnEscapeKeypress: (e: any) => void,
  showCloseButtonInBody?: boolean,
  title?: string | ReactNode,
  onClose?: () => void,
  navigationNote?: Key,
  isMidiKeyboardConnected?: boolean,
  isBodyPadded?: boolean) {
  return (
    <>
      { title || (onClose && !showCloseButtonInBody)
        ?
        <header className={`${s.titleContainer} ${!title && onClose ? s.noTitle : ''}`}>
          {title
            ? <div className={s.title}>{title}</div>
            : null
          }
          {onClose && !showCloseButtonInBody
            ?
            getCloseButton(closeOnEscapeKeypress, onClose, navigationNote, "header", isMidiKeyboardConnected, isBodyPadded)
            :
            null
          }
        </header>
        :
        null
      }
    </>
  );
}

function getCloseButton(
  closeOnEscapeKeypress: (e: any) => void,
  onClose?: () => void,
  navigationNote?: Key,
  renderLocation: "header" | "body" = "header",
  isMidiKeyboardConnected?: boolean,
  isBodyPadded?: boolean) {

  let closeButtonCssClasses = `${s.closeButton} ${navigationNote && isMidiKeyboardConnected ? s.withNavigationNote : ''}`;
  closeButtonCssClasses += renderLocation === "body" ? ` ${s.forBody}` : ` ${s.forHeader}`;
  if (!isBodyPadded) {
    closeButtonCssClasses += ` ${s.noBodyPadding}`;
  }

  let navigationNoteCssClasses = `${s.navigationNote} ${renderLocation === "body" ? s.forBody : s.forHeader}`;
  if (!isBodyPadded) {
    navigationNoteCssClasses += ` ${s.noBodyPadding}`;
  }

  return (
    <>
      <button
        className={closeButtonCssClasses}
        onClick={onClose}
        onKeyUp={closeOnEscapeKeypress}
        aria-label="Close"
        title={"Close"}>
        <img alt={"Close"} src={iconImages.closeButton}/>
      </button>
      {
        navigationNote && isMidiKeyboardConnected
          ?
          <div className={navigationNoteCssClasses}>
            <PianoKey pianoKey={navigationNote}/>
          </div>
          :
          null
      }
    </>
  );
}